import { useCallback, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { useAutosave } from '../hooks/useAutosave';
import { useCardSaveBadges } from '../hooks/useCardSaveBadges';
import { CardSaveBadge } from '../components/tenant/CardSaveBadge';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ExternalLink, Loader2 } from 'lucide-react';
import { UNIT_ANALYSIS_VIBES } from '../constants/unitAnalysisVibes';
import UnitPhotoCarousel from '../components/tenant/UnitPhotoCarousel';
import { unitAnalysisProgress } from '../utils/prospect-sidebar-progress';
import { formatTourBedBath } from '../utils/tour-home';
import VibeWordCloud from '../components/tenant/VibeWordCloud';
import { useSetPageProgress } from '../contexts/ProspectPageProgressContext';
import { useTenantApiToken } from '../contexts/TenantProfileContext';
import {
  clearProspectUnitAnalysis,
  getProspectUnitAnalyses,
  saveProspectUnitAnalysis,
  type ProspectUnitAnalysisPage,
  type ProspectUnitAnalysisResponse,
  type ProspectUnitAnalysisUnit,
} from '../services/tenantApi';

const ALL_UNITS_TAB = 'all-units';

const SCORE_HOVER_GUIDES: Record<number, string> = {
  1: 'This feature is a deal breaker.',
  2: 'This would be hard to live with.',
  3: 'I could live with it.',
  4: "It's a bit of a letdown.",
  5: "It's fine.",
  6: 'I like this.',
  7: 'This is pretty great.',
  8: 'This is excellent.',
  9: 'I love this.',
  10: "I'm obsessed.",
};

const PAGE_SHELL = 'mx-auto w-full max-w-lg md:max-w-3xl lg:max-w-none';
const SECTION_CARD =
  'rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 px-4 py-4 shadow-bento sm:px-6 sm:py-5';

const SCORE_FIELDS = [
  { key: 'kitchen', label: 'Kitchen' },
  { key: 'livingRoom', label: 'Living room' },
  { key: 'bedrooms', label: 'Bedrooms' },
  { key: 'bathrooms', label: 'Bathrooms' },
  { key: 'laundry', label: 'Laundry' },
  { key: 'location', label: 'Location' },
  { key: 'vibes', label: 'Vibes' },
] as const;

/** One row of 1–10; cells shrink with column width instead of wrapping. */
const SCORE_ROW_GRID = 'mt-2 grid w-full grid-cols-10 gap-0.5 sm:gap-1';
const SCORE_CELL_BUTTON =
  'group relative flex aspect-square w-full min-w-0 max-h-9 items-center justify-center rounded-md text-[11px] font-medium leading-none sm:text-xs';

type ScoreKey = (typeof SCORE_FIELDS)[number]['key'];

interface AnalysisDraft {
  kitchen: number | null;
  livingRoom: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  laundry: number | null;
  location: number | null;
  vibes: number | null;
  phrases: string[];
}

function emptyDraft(saved: AnalysisDraft | ProspectUnitAnalysisResponse | null): AnalysisDraft {
  return {
    kitchen: saved?.kitchen ?? null,
    livingRoom: saved?.livingRoom ?? null,
    bedrooms: saved?.bedrooms ?? null,
    bathrooms: saved?.bathrooms ?? null,
    laundry: saved?.laundry ?? null,
    location: saved?.location ?? null,
    vibes: saved?.vibes ?? null,
    phrases: saved?.phrases ?? [],
  };
}

function phraseKey(value: string): string {
  return value.trim().toLocaleLowerCase();
}

function hasPhrase(phrases: string[], phrase: string): boolean {
  const key = phraseKey(phrase);
  return phrases.some((item) => phraseKey(item) === key);
}

function togglePhrase(phrases: string[], phrase: string): string[] {
  const key = phraseKey(phrase);
  if (phrases.some((item) => phraseKey(item) === key)) {
    return phrases.filter((item) => phraseKey(item) !== key);
  }
  return [...phrases, phrase];
}

function completedResponse(draft: AnalysisDraft): ProspectUnitAnalysisResponse | null {
  const scores = {} as Record<ScoreKey, number>;
  for (const field of SCORE_FIELDS) {
    const value = draft[field.key];
    if (value == null) return null;
    scores[field.key] = value;
  }
  return { ...scores, phrases: draft.phrases };
}

function analysisSnapshot(unitId: string, draft: AnalysisDraft): string {
  return JSON.stringify({ unitId, draft });
}

function draftHasContent(draft: AnalysisDraft): boolean {
  return SCORE_FIELDS.some((field) => draft[field.key] != null) || draft.phrases.length > 0;
}

function patchAnalysisUnit(
  page: ProspectUnitAnalysisPage,
  unitId: string,
  next: { draft: AnalysisDraft | null; mine: ProspectUnitAnalysisResponse | null },
): ProspectUnitAnalysisPage {
  return {
    ...page,
    units: page.units.map((unit) => (
      unit.unitId === unitId ? { ...unit, draft: next.draft, mine: next.mine } : unit
    )),
  };
}

function formatAnalysisHomeTitle(unitNumber: string, propertyName: string): string {
  const number = unitNumber.trim().replace(/^#/, '');
  return `#${number} - ${propertyName.trim()}`;
}

function AnalyzeUnitNav({
  units,
  selectedUnitId,
  showingAllUnits,
  onAllUnits,
  onSelectUnit,
}: {
  units: ProspectUnitAnalysisUnit[];
  selectedUnitId: string | null;
  showingAllUnits: boolean;
  onAllUnits: () => void;
  onSelectUnit: (unit: ProspectUnitAnalysisUnit) => void;
}): ReactElement {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState({ hasOverflow: false, canLeft: false, canRight: false });

  const refreshScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const hasOverflow = el.scrollWidth > el.clientWidth + 4;
    setScrollState({
      hasOverflow,
      canLeft: hasOverflow && el.scrollLeft > 8,
      canRight: hasOverflow && el.scrollLeft + el.clientWidth < el.scrollWidth - 8,
    });
  }, []);

  useEffect(() => {
    refreshScrollState();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener('scroll', refreshScrollState, { passive: true });
    const observer = new ResizeObserver(refreshScrollState);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', refreshScrollState);
      observer.disconnect();
    };
  }, [units.length, refreshScrollState]);

  const scrollHomes = (direction: -1 | 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const step = Math.max(200, Math.round(el.clientWidth * 0.65));
    el.scrollBy({ left: direction * step, behavior: 'smooth' });
  };

  return (
    <div className="mt-4 flex min-w-0 items-center gap-1.5">
      <UnitNavPill selected={showingAllUnits} onClick={onAllUnits}>
        All units
      </UnitNavPill>
      {scrollState.hasOverflow ? (
        <button
          type="button"
          aria-label="Scroll homes left"
          disabled={!scrollState.canLeft}
          onClick={() => scrollHomes(-1)}
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 transition-colors hover:border-gray-300 disabled:cursor-default disabled:opacity-35 dark:border-white/15 dark:bg-gray-900 dark:text-gray-200 dark:hover:border-white/25"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </button>
      ) : null}
      <div
        ref={scrollerRef}
        role="tablist"
        aria-label="Homes to analyze"
        className="flex min-w-0 flex-1 snap-x snap-mandatory gap-2 overflow-x-auto overscroll-x-contain pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {units.map((unit) => {
          const title = formatAnalysisHomeTitle(unit.unitNumber, unit.propertyName);
          const open = !showingAllUnits && selectedUnitId === unit.unitId;
          return (
            <UnitNavPill
              key={unit.unitId}
              selected={open}
              disabled={!unit.canAnalyze}
              title={unit.canAnalyze ? title : 'You can analyze this home after the tour'}
              onClick={() => onSelectUnit(unit)}
            >
              {unit.imageUrl ? (
                <span className="h-5 w-7 shrink-0 overflow-hidden rounded bg-gray-200 dark:bg-gray-700">
                  <img src={unit.imageUrl} alt="" className="h-full w-full object-cover" />
                </span>
              ) : null}
              <span className="max-w-[11rem] truncate sm:max-w-[14rem]">{title}</span>
            </UnitNavPill>
          );
        })}
      </div>
      {scrollState.hasOverflow ? (
        <button
          type="button"
          aria-label="Scroll homes right"
          disabled={!scrollState.canRight}
          onClick={() => scrollHomes(1)}
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 transition-colors hover:border-gray-300 disabled:cursor-default disabled:opacity-35 dark:border-white/15 dark:bg-gray-900 dark:text-gray-200 dark:hover:border-white/25"
        >
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

function UnitNavPill({
  selected,
  disabled,
  title,
  onClick,
  children,
}: {
  selected: boolean;
  disabled?: boolean;
  title?: string;
  onClick: () => void;
  children: ReactNode;
}): ReactElement {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      disabled={disabled}
      title={title}
      onClick={onClick}
      className={`inline-flex max-w-full shrink-0 snap-start items-center gap-1.5 rounded-full border px-3 py-1.5 text-left text-sm font-medium transition-colors ${
        selected
          ? 'border-red-600 bg-red-50 text-red-800 dark:border-red-500 dark:bg-red-950/40 dark:text-red-200'
          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-white/15 dark:bg-gray-900 dark:text-gray-200 dark:hover:border-white/25'
      } disabled:cursor-not-allowed disabled:opacity-45`}
    >
      {children}
    </button>
  );
}

function formatEasternTourTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const day = date.toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const time = date.toLocaleTimeString('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
  return `${day}, ${time}`;
}

function suggestionKeys(): Set<string> {
  return new Set(UNIT_ANALYSIS_VIBES.map((vibe) => phraseKey(vibe)));
}

export default function AnalyzeUnitsPage(): ReactElement {
  const getToken = useTenantApiToken();
  const setPageProgress = useSetPageProgress();
  const [searchParams] = useSearchParams();
  const requestedUnitId = searchParams.get('unit');
  const appliedUnitQuery = useRef<string | null>(null);
  const [page, setPage] = useState<Awaited<ReturnType<typeof getProspectUnitAnalyses>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const [draft, setDraft] = useState<AnalysisDraft | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const next = await getProspectUnitAnalyses(getToken);
      setPage(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load homes to analyze.');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!page) return;
    setPageProgress(
      '/analyze-units',
      unitAnalysisProgress(
        page.units.map((unit) => ({
          availability: unit.availability,
          analyzed: unit.mine != null,
        })),
      ),
    );
  }, [page, setPageProgress]);

  const showingAllUnits = selectedUnitId === ALL_UNITS_TAB;
  const selected = page?.units.find((unit) => unit.unitId === selectedUnitId && unit.canAnalyze) ?? null;

  useEffect(() => {
    if (!page) return;
    if (requestedUnitId && appliedUnitQuery.current !== requestedUnitId) {
      appliedUnitQuery.current = requestedUnitId;
      const requested = page.units.find((unit) => unit.unitId === requestedUnitId && unit.canAnalyze);
      if (requested) {
        setSelectedUnitId(requested.unitId);
        setDraft(emptyDraft(requested.draft ?? requested.mine));
        return;
      }
    }
    if (selectedUnitId) return;
    const first = page.units.find((unit) => unit.canAnalyze);
    if (!first) return;
    setSelectedUnitId(first.unitId);
    setDraft(emptyDraft(first.draft ?? first.mine));
  }, [page, requestedUnitId, selectedUnitId]);

  const analysisSnapshotValue = selected && draft ? analysisSnapshot(selected.unitId, draft) : null;
  const storedAnalysisSnapshot = selected
    ? analysisSnapshot(selected.unitId, emptyDraft(selected.draft ?? selected.mine))
    : null;
  const analysisSave = useAutosave({
    snapshot: analysisSnapshotValue,
    ready: Boolean(page && selected && draft),
    enabled: Boolean(selected),
    resetKey: selected?.unitId ?? null,
    baselineSnapshot: storedAnalysisSnapshot,
    save: async (snapshot, context) => {
      const parsed = JSON.parse(snapshot) as { unitId: string; draft: AnalysisDraft };
      const response = completedResponse(parsed.draft);
      if (!draftHasContent(parsed.draft)) {
        await clearProspectUnitAnalysis(getToken, parsed.unitId, { keepalive: context.keepalive });
        setPage((current) => (
          current ? patchAnalysisUnit(current, parsed.unitId, { draft: null, mine: null }) : current
        ));
        return;
      }
      await saveProspectUnitAnalysis(getToken, parsed.unitId, parsed.draft, {
        keepalive: context.keepalive,
      });
      setPage((current) => (
        current
          ? patchAnalysisUnit(current, parsed.unitId, { draft: parsed.draft, mine: response })
          : current
      ));
    },
    onSaved: () => setError(null),
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Unable to save that analysis.');
    },
  });

  const analysisCardSlices = {
    scores: JSON.stringify(SCORE_FIELDS.map((field) => draft?.[field.key] ?? null)),
    phrases: JSON.stringify(draft?.phrases ?? []),
  };
  const storedAnalysisDraft = selected ? emptyDraft(selected.draft ?? selected.mine) : null;
  const analysisCardSave = useCardSaveBadges(
    analysisCardSlices,
    analysisSave.status,
    Boolean(page && selected && draft),
    selected?.unitId ?? null,
    storedAnalysisDraft
      ? {
          scores: JSON.stringify(SCORE_FIELDS.map((field) => storedAnalysisDraft[field.key])),
          phrases: JSON.stringify(storedAnalysisDraft.phrases),
        }
      : null,
  );

  const selectUnit = (unit: ProspectUnitAnalysisUnit) => {
    if (!unit.canAnalyze || unit.unitId === selectedUnitId) return;
    void (async () => {
      await analysisSave.flush();
      setSelectedUnitId(unit.unitId);
      setDraft(emptyDraft(unit.draft ?? unit.mine));
      setError(null);
    })();
  };

  const openAllUnits = () => {
    void (async () => {
      await analysisSave.flush();
      setSelectedUnitId(ALL_UNITS_TAB);
      setError(null);
    })();
  };

  const addCustomPhrase = (raw: string) => {
    const phrase = raw.trim().replace(/\s+/g, ' ');
    if (!phrase) return;
    setDraft((current) => {
      if (!current || current.phrases.length >= 40 || hasPhrase(current.phrases, phrase)) return current;
      return { ...current, phrases: [...current.phrases, phrase] };
    });
  };

  const knownVibes = suggestionKeys();
  const customPhrases = draft?.phrases.filter((phrase) => !knownVibes.has(phraseKey(phrase))) ?? [];

  return (
    <div className={`${PAGE_SHELL} flex flex-col gap-4 pb-2 sm:pb-4 lg:grid lg:grid-cols-12 lg:items-start`}>
      <header className={`lg:col-span-12 ${SECTION_CARD}`}>
        <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">Step 5</p>
        <h1 className="mt-1 text-2xl font-bold text-gray-900 dark:text-white sm:text-3xl">Analyze Units</h1>
        <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400 sm:text-base">
          Think about what you liked about this home.
        </p>
        {!loading && page && page.units.length > 0 ? (
          <AnalyzeUnitNav
            units={page.units}
            selectedUnitId={selectedUnitId}
            showingAllUnits={showingAllUnits}
            onAllUnits={openAllUnits}
            onSelectUnit={selectUnit}
          />
        ) : null}
      </header>

      {error ? (
        <p className="rounded-2xl border border-rose-200 dark:border-rose-900/40 bg-rose-50 dark:bg-rose-950/30 px-4 py-3 text-sm text-rose-700 dark:text-rose-300 lg:col-span-12">
          {error}
        </p>
      ) : null}

      {loading ? (
        <p className={`flex items-center gap-2 text-sm text-gray-500 lg:col-span-12 ${SECTION_CARD}`}>
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading homes…
        </p>
      ) : null}

      {!loading && (page?.units.length ?? 0) === 0 ? (
        <div className={`lg:col-span-12 ${SECTION_CARD}`}>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Book a tour first. After you have completed a tour, the unit will become available to analyze here.
          </p>
        </div>
      ) : null}

      {!loading && page && page.units.length > 0 ? (
        <div className="flex flex-col gap-4 lg:col-span-12 lg:grid lg:grid-cols-12 lg:items-stretch lg:gap-4">
          <section
            className={`relative z-0 min-w-0 ${SECTION_CARD} ${showingAllUnits ? 'lg:col-span-12' : 'lg:col-span-4'}`}
          >
            {showingAllUnits || !selected ? null : (
              <CardSaveBadge phase={analysisCardSave.scores} complete={selected.mine != null} />
            )}
            {showingAllUnits ? (
              <AllUnitsAnalysis
                units={page.units.filter((unit) => unit.availability === 'ready')}
                onOpen={selectUnit}
              />
            ) : selected ? (
              <>
                <div className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-1 pr-10 text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatTourBedBath(selected.bedrooms, selected.bathrooms)}
                  </span>
                  <span>Toured {formatEasternTourTime(selected.tourStartAt)}</span>
                  {selected.mine ? <span className="font-medium text-emerald-700">Analyzed</span> : null}
                </div>
                {draft ? (
                  <YourRatingScores
                    draft={draft}
                    onScore={(key, value) =>
                      setDraft((current) =>
                        current ? { ...current, [key]: current[key] === value ? null : value } : current,
                      )
                    }
                  />
                ) : null}
              </>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">Open a home to rate it.</p>
            )}
          </section>

          {showingAllUnits || !selected ? null : (
            <div className="flex flex-col gap-4 lg:col-span-8 lg:min-h-0">
              <MediaBento unit={selected} />
              {draft ? (
                <VibeWordsBento
                  savePhase={analysisCardSave.phrases}
                  customPhrases={customPhrases}
                  draft={draft}
                  onTogglePhrase={(phrase) =>
                    setDraft((current) =>
                      current ? { ...current, phrases: togglePhrase(current.phrases, phrase) } : current,
                    )
                  }
                  onAddPhrase={addCustomPhrase}
                />
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

const MEDIA_LANDSCAPE_FRAME =
  'relative aspect-video w-full overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-800';

type MediaTab = 'photos' | 'tour';

function MediaBento({ unit }: { unit: ProspectUnitAnalysisUnit }): ReactElement {
  const [mediaTab, setMediaTab] = useState<MediaTab>('photos');
  const title = formatAnalysisHomeTitle(unit.unitNumber, unit.propertyName);
  const slides = unit.photos.length > 0 ? unit.photos : unit.imageUrl ? [unit.imageUrl] : [];

  useEffect(() => {
    setMediaTab('photos');
  }, [unit.unitId]);

  return (
    <div className="flex min-h-0 flex-col rounded-2xl shadow-bento">
      <div role="tablist" aria-label="Photos and 3D tour" className="chrome-tablist relative z-10 flex items-end">
        <button
          type="button"
          role="tab"
          aria-selected={mediaTab === 'photos'}
          onClick={() => setMediaTab('photos')}
          className="chrome-tab min-w-0 text-left text-sm"
        >
          Photos
        </button>
        <span className="chrome-tab-divider" aria-hidden />
        <button
          type="button"
          role="tab"
          aria-selected={mediaTab === 'tour'}
          onClick={() => setMediaTab('tour')}
          className="chrome-tab min-w-0 text-left text-sm"
        >
          3D tour
        </button>
      </div>
      <section className="relative z-0 rounded-b-2xl border border-t-0 border-gray-200 bg-white px-4 py-4 dark:border-white/10 dark:bg-gray-800 sm:px-5 sm:py-5">
        {mediaTab === 'photos' ? (
          <UnitPhotoCarousel key={unit.unitId} photos={slides} label={title} className={MEDIA_LANDSCAPE_FRAME} />
        ) : (
          <div className={MEDIA_LANDSCAPE_FRAME}>
            {unit.matterportUrl ? (
              <>
                <iframe
                  src={unit.matterportUrl}
                  title={`${title} Matterport tour`}
                  className="absolute inset-0 h-full w-full"
                  allowFullScreen
                  allow="xr-spatial-tracking"
                />
                <a
                  href={unit.matterportUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute right-2 top-2 z-10 inline-flex items-center gap-1 rounded-full bg-white/90 px-2.5 py-1 text-xs font-medium text-gray-900 shadow dark:text-white"
                >
                  Open
                  <ExternalLink className="h-3 w-3" />
                </a>
              </>
            ) : (
              <p className="absolute inset-0 flex items-center justify-center px-4 text-center text-sm text-gray-500 dark:text-gray-400">
                No Matterport tour for this home.
              </p>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function YourRatingScores({
  draft,
  onScore,
}: {
  draft: AnalysisDraft;
  onScore: (key: ScoreKey, value: number) => void;
}): ReactElement {
  return (
    <div className="rounded-2xl bg-sky-50 px-4 py-4 dark:bg-sky-950/30 sm:px-5 sm:py-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Your rating</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Rate each part from 1 to 10. Click a score again to clear it.
        </p>
      </div>
      <div className="mt-6 flex w-full flex-col gap-4">
        {SCORE_FIELDS.map((field) => (
          <div key={field.key} className="w-full">
            <p className="text-sm font-medium text-gray-900 dark:text-white">{field.label}</p>
            <div className={SCORE_ROW_GRID}>
              {Array.from({ length: 10 }, (_, index) => index + 1).map((score) => {
                const active = draft[field.key] === score;
                const guide = SCORE_HOVER_GUIDES[score];
                return (
                  <button
                    key={score}
                    type="button"
                    aria-pressed={active}
                    aria-label={guide ? `${field.label} ${score}. ${guide}` : `${field.label} ${score}`}
                    onClick={() => onScore(field.key, score)}
                    className={`${SCORE_CELL_BUTTON} ${
                      active
                        ? 'bg-gray-900 text-white'
                        : 'border border-gray-200 bg-white text-gray-700 hover:border-gray-400 dark:border-white/15 dark:bg-gray-950 dark:text-gray-200 dark:hover:border-white/30'
                    }`}
                  >
                    {score}
                    {guide ? (
                      <span className="pointer-events-none absolute bottom-[calc(100%+0.45rem)] left-1/2 z-30 hidden w-max max-w-[13rem] -translate-x-1/2 rounded-lg bg-gray-900 px-2.5 py-1.5 text-center text-xs font-medium leading-snug text-white shadow-md group-hover:block">
                        {guide}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function VibeWordsBento({
  draft,
  customPhrases,
  onTogglePhrase,
  onAddPhrase,
  savePhase,
}: {
  draft: AnalysisDraft;
  customPhrases: string[];
  onTogglePhrase: (phrase: string) => void;
  onAddPhrase: (phrase: string) => void;
  savePhase: 'idle' | 'saving' | 'saved';
}): ReactElement {
  return (
    <section className={`relative ${SECTION_CARD}`}>
      <CardSaveBadge phase={savePhase} />
      <h2 className="pr-8 text-lg font-semibold text-gray-900 dark:text-white">Vibe words</h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Hover a word to bring it forward, then click the ones that fit. Use + to add your own.
      </p>
      <VibeWordCloud
        customPhrases={customPhrases}
        isSelected={(phrase) => hasPhrase(draft.phrases, phrase)}
        onToggle={onTogglePhrase}
        onAdd={onAddPhrase}
      />
    </section>
  );
}

function AllUnitsAnalysis({
  units,
  onOpen,
}: {
  units: ProspectUnitAnalysisUnit[];
  onOpen: (unit: ProspectUnitAnalysisUnit) => void;
}): ReactElement {
  return <GroupAverageTable units={units} onOpen={onOpen} />;
}

function GroupAverageTable({
  units,
  onOpen,
}: {
  units: ProspectUnitAnalysisUnit[];
  onOpen: (unit: ProspectUnitAnalysisUnit) => void;
}): ReactElement {
  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Group averages</h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">How your group scored each home it has already seen.</p>
      {units.length === 0 ? (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Averages show up here after a tour has ended.</p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[40rem] border-separate border-spacing-0 text-sm">
            <thead>
              <tr>
                <th className="border-b border-gray-200 dark:border-white/10 px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                  Home
                </th>
                {SCORE_FIELDS.map((field) => (
                  <th
                    key={field.key}
                    className="border-b border-gray-200 dark:border-white/10 px-2 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500"
                  >
                    {field.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {units.map((unit) => {
                const title = formatAnalysisHomeTitle(unit.unitNumber, unit.propertyName);
                const people = unit.group?.submittedCount ?? 0;
                return (
                  <tr key={unit.unitId}>
                    <th className="border-b border-gray-100 dark:border-white/5 px-3 py-5 text-left font-medium text-gray-900 dark:text-white">
                      <button
                        type="button"
                        onClick={() => onOpen(unit)}
                        className="flex items-center gap-3 text-left"
                      >
                        <span className="h-14 w-[4.5rem] shrink-0 overflow-hidden rounded-lg bg-gray-200">
                          {unit.imageUrl ? (
                            <img src={unit.imageUrl} alt="" className="h-full w-full object-cover" />
                          ) : null}
                        </span>
                        <span>
                          <span className="block text-base">{title}</span>
                          <span className="mt-0.5 block text-xs font-normal text-gray-500 dark:text-gray-400">
                            {people === 0 ? 'No scores yet' : people === 1 ? '1 person' : `${people} people`}
                          </span>
                        </span>
                      </button>
                    </th>
                    {SCORE_FIELDS.map((field) => {
                      const average = unit.group?.averages[field.key] ?? null;
                      return (
                        <td
                          key={field.key}
                          className="border-b border-gray-100 dark:border-white/5 px-2 py-5 text-center text-base tabular-nums text-gray-800 dark:text-gray-100"
                        >
                          {average == null ? <span className="text-gray-300 dark:text-gray-500">—</span> : formatGroupAverage(average)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function formatGroupAverage(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}
